import React from 'react';
import type { GuideBlueprintPreview } from '@interactive-room';
import type { CurriculumTrace } from '../constants/examAiParams';

interface GuidePlanningTraceProps {
  trace: CurriculumTrace | null;
  blueprint: GuideBlueprintPreview | null;
  loading?: boolean;
  generationMode: 'curricular' | 'independent';
  hasIntent: boolean;
  variant?: 'inline' | 'sidebar';
}

export function GuidePlanningTrace({
  trace,
  blueprint,
  loading = false,
  generationMode,
  hasIntent,
  variant = 'inline',
}: GuidePlanningTraceProps) {
  const isSidebar = variant === 'sidebar';
  const shellClass = isSidebar ? 'space-y-5' : 'space-y-3 rounded-lg border border-slate-200 bg-white p-4';

  if (generationMode === 'independent') {
    if (!hasIntent) {
      return (
        <div className={shellClass}>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Plan de generación
            </p>
            <p className="mt-2 text-sm text-slate-600">
              Escribe la intención de contenido para ver cómo se estructurará la guía.
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  const showBlueprint = Boolean(blueprint?.slots?.length);
  const showTrace = Boolean(trace?.oaCode);
  const showMismatch = Boolean(blueprint?.intentMismatch?.message);

  if (!showTrace && !showBlueprint && !loading && !showMismatch) {
    return (
      <div className={shellClass}>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Trazabilidad curricular
          </p>
          <p className="mt-2 text-sm text-slate-600">
            Selecciona un OA y describe tu intención para ver el plan pedagógico alineado al MINEDUC.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      {showMismatch && blueprint?.intentMismatch && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {blueprint.intentMismatch.message}
        </div>
      )}

      {showTrace && trace && (
        <section className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Escudo seleccionado
            </p>
            {loading && <span className="text-[10px] text-slate-400">Actualizando…</span>}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">
              {trace.axisLabel} · {trace.oaCode}
              {trace.oahCode ? ` · ${trace.oahCode}` : ''}
              {trace.oaaCode ? ` · ${trace.oaaCode}` : ''}
            </p>
            {trace.oaText && <p className="mt-2 leading-relaxed text-slate-600">{trace.oaText}</p>}
            {!trace.oaText && trace.oaSummary && (
              <p className="mt-2 leading-relaxed text-slate-600">{trace.oaSummary}</p>
            )}
            {blueprint?.suggestedCurriculumTarget &&
              !trace.oaCode &&
              blueprint.suggestedCurriculumTarget.oaCode && (
                <p className="mt-2 text-xs text-emerald-700">
                  OA sugerido por arquetipo: {blueprint.suggestedCurriculumTarget.oaCode}
                </p>
              )}
            {!hasIntent && (
              <p className="mt-2 text-xs text-amber-700">
                Añade intención de contenido para afinar metáforas, alcance numérico y secuencia de
                bloques.
              </p>
            )}
          </div>
        </section>
      )}

      {showBlueprint && blueprint && (
        <section className="space-y-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Plan de generación
            </p>
            <p className="mt-1 text-sm text-slate-600">
              <strong className="text-slate-900">{blueprint.conceptLabel}</strong> ·{' '}
              {blueprint.blockCount} bloques
              {blueprint.includeReflection ? ' · cierre con reflexión escrita' : ''}
            </p>
            {blueprint.sessionPlanning ? (
              <p className="mt-1 text-xs text-emerald-700">
                Contexto leído del plan: {blueprint.sessionPlanning.entorno}
                {blueprint.sessionPlanning.measureCount > 0
                  ? ` · ${blueprint.sessionPlanning.measureCount} medida(s) del docente`
                  : ' · plantilla por entorno'}
                {blueprint.sessionPlanning.source === 'teacher_plan'
                  ? ' · alineado a tu sesión'
                  : ''}
              </p>
            ) : null}
          </div>
          {blueprint.archetypeHints && blueprint.archetypeHints.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-4 text-xs text-slate-500">
              {blueprint.archetypeHints.map((hint) => (
                <li key={hint}>{hint}</li>
              ))}
            </ul>
          )}
          <ol className="max-h-52 space-y-2 overflow-y-auto text-xs">
            {blueprint.slots.map((slot) => (
              <li
                key={`${slot.index}-${slot.purpose}`}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2"
              >
                <span className="font-semibold text-slate-900">
                  {slot.index + 1}. {slot.modeLabel}
                </span>
                <span className="text-slate-500"> · {slot.phaseLabel}</span>
                <span className="mt-0.5 block text-slate-500">{slot.hint}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {!showBlueprint && !loading && hasIntent && showTrace && (
        <p className="text-xs text-slate-500">
          No hay plan MINEDUC predefinido para esta combinación — la guía usará el escudo curricular
          y tu intención docente.
        </p>
      )}
    </div>
  );
}
